import React from "react";
import styled, { css } from "styled-components";
import { Density, controlHeight, useDensity } from "./base/density";
import { bodyType, colorTransition, disabledState, focusRing, iconSlot, unselectable } from "./base/mixins";

export type ButtonVariant = "default" | "primary" | "secondary";

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
	/**
	 * `default` — white, 1px border. Everything that is not the main action.
	 * `primary` — lime fill, dark label. The single main action on a view.
	 * `secondary` — purple fill, white label. A co-equal alternative, where one
	 *   is genuinely needed. Never two filled buttons of the same weight side by side.
	 */
	variant?: ButtonVariant;
	/**
	 * Marks the button as an *action* rather than a *tool* — import, save, export,
	 * add. Tints the icon purple (§6). Ignored on a filled variant, where the icon
	 * inherits the label like everything else.
	 */
	action?: boolean;
	/** Auto-width, 40px. For buttons that sit next to text or in a row. */
	inline?: boolean;
	/** Overrides the density inherited from an enclosing Panel. */
	density?: Density;
	icon?: React.ReactNode;
	children?: React.ReactNode;
}

/**
 * DESIGN_SYSTEM.md §8. Full width of its container by default, icon left then label.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ variant = "default", action = false, inline = false, density, icon, children, ...rest }, ref) => {
		const resolved = useDensity(density);
		return (
			<StyledButton
				ref={ref}
				type="button"
				{...rest}
				$variant={variant}
				$action={action && variant === "default"}
				$inline={inline}
				$height={inline ? "var(--size-control-compact)" : controlHeight(resolved)}
			>
				{icon ? <span className="hds-btn__icon">{icon}</span> : null}
				{children}
			</StyledButton>
		);
	},
);

Button.displayName = "Button";

const filled = (fill: string, hover: string, active: string, label: string) => css`
	background: var(${fill});
	border-color: var(${fill});
	color: var(${label});

	&:hover:not(:disabled) {
		background: var(${hover});
		border-color: var(${hover});
	}
	&:active:not(:disabled) {
		background: var(${active});
		border-color: var(${active});
	}
`;

const StyledButton = styled.button<{
	$variant: ButtonVariant;
	$action: boolean;
	$inline: boolean;
	$height: string;
}>`
	${unselectable}
	display: flex;
	align-items: center;
	gap: var(--space-4);
	width: ${({ $inline }) => ($inline ? "auto" : "100%")};
	/* Without this a flex column stretches an inline button back to full width. */
	align-self: ${({ $inline }) => ($inline ? "flex-start" : "auto")};
	height: ${({ $height }) => $height};
	padding: 0 ${({ $inline }) => ($inline ? "var(--space-4)" : "var(--space-5)")};
	border: var(--border-default);
	border-radius: var(--radius-md);
	background: var(--color-surface);
	cursor: pointer;
	box-shadow: none;
	${bodyType}
	${colorTransition}
	${focusRing}
	${disabledState}

	.hds-btn__icon {
		${iconSlot}
		color: ${({ $action }) => ($action ? "var(--color-secondary)" : "currentColor")};
	}

	&:hover:not(:disabled) {
		background: var(--color-hover-ground);
		border-color: var(--color-border-strong);
	}

	${({ $variant }) =>
		$variant === "primary" &&
		filled(
			"--color-primary-fill",
			"--color-primary-fill-hover",
			"--color-primary-fill-active",
			"--color-text-on-primary",
		)}

	${({ $variant }) =>
		$variant === "secondary" &&
		filled(
			"--color-secondary-fill",
			"--color-secondary-fill-hover",
			"--color-secondary-fill-active",
			"--color-text-on-secondary",
		)}
`;

export default Button;
